import * as styles from './index.css';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { activeRouteStore } from '@customerio/hydra-nav-state';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useStore } from '@nanostores/react';

import { useCallback, useMemo, useRef } from 'react';
import { useCurrentWorkspace } from '../../../../context/current-workspace';
import type { NavItem as NavItemType, NavItemGroup } from './routes';
import { generateRoutes, isNavItemActive } from './routes';
import { isEmptyString } from '@/utils/string';

import { shouldProcessLinkClick } from '../../../../utils/navigation';
import { useIsSettings } from '@/services/router/use-is-settings';

import { useListWorkspaceObjectTypes } from '@/features/workspaces';
import { useAuth } from '@/services/auth';
import { useAnalytics } from '@/services/analytics';
import {
  SideNavGroup,
  NavItem,
  NavItemExpandable,
  NavItemExpandableTrigger,
  NavItemExpandableContent,
  NavItemDropdown,
  NavItemDropdownTrigger,
  NavItemDropdownContent,
  Box,
  Label,
  InlineStack,
  Tooltip,
  useSideNavState,
} from '@customerio/pluma-components/react';

export function ExtendedNavItem(props: {
  item: NavItemType;
  onClick: (
    event: React.MouseEvent | undefined,
    item: NavItemType,
    section: string | null,
    parentItem: string | null,
  ) => unknown;
  section: string | null;
  parentItem?: string | null;
}) {
  const { item, onClick, section, parentItem = null } = props;

  const activeRoute = useStore(activeRouteStore);

  const {
    iconName,
    href,
    label,
    id,
    showPremiumLabel = false,
    showNewLabel = false,
    isGreyedOut = false,
    isDisabled = false,
    isVisuallyDisabled = false,
    disabledTooltip,
    children,
    ariaLabel,
  } = item;

  if (item.isDropdownMenu === true && children != null) {
    if (children.length === 0) {
      return null;
    }

    if (children.length === 1) {
      return <ExtendedNavItem item={children[0]} onClick={onClick} section={section} parentItem={item.label} />;
    }

    return (
      <NavItemDropdown>
        <NavItemDropdownContent>
          {children.map((child) => (
            <ExtendedNavItem key={child.id} item={child} onClick={onClick} section={section} parentItem={item.label} />
          ))}
        </NavItemDropdownContent>

        <NavItemDropdownTrigger aria-label={ariaLabel} icon={iconName}>
          {label}
        </NavItemDropdownTrigger>
      </NavItemDropdown>
    );
  }

  if (children != null) {
    return (
      <NavItemExpandable key={item.id} defaultIsOpen={item.defaultIsOpen}>
        <NavItemExpandableTrigger icon={iconName}>{label}</NavItemExpandableTrigger>

        <NavItemExpandableContent>
          {children.map((child) => {
            return (
              <ExtendedNavItem
                key={child.id}
                item={child}
                onClick={onClick}
                section={section}
                parentItem={item.label}
              />
            );
          })}
        </NavItemExpandableContent>
      </NavItemExpandable>
    );
  }

  const navItem = (
    <NavItem
      data-test-nav-item={id}
      href={isDisabled ? undefined : href}
      icon={iconName}
      isSelected={isNavItemActive(item, activeRoute)}
      onClick={(event) => {
        if (isDisabled) {
          event?.preventDefault();
          return;
        }
        onClick(event, item, section, parentItem);
      }}
      aria-disabled={isDisabled ? true : undefined}
      tabIndex={isDisabled ? -1 : undefined}
    >
      <InlineStack gap="050" alignBlock="center">
        <Box as="span" truncate={true}>
          {label}
        </Box>
        {showNewLabel ? (
          <Label color="purple" flexShrink="0">
            New
          </Label>
        ) : null}
        {showPremiumLabel ? (
          <Label color="plum" flexShrink="0">
            Premium
          </Label>
        ) : null}
      </InlineStack>
    </NavItem>
  );

  if (isDisabled) {
    if (!isEmptyString(disabledTooltip)) {
      return (
        <Tooltip content={disabledTooltip} placement="right" isCustom={true}>
          {(context) => (
            <div
              className={styles.disabledWrapper}
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
            >
              {navItem}
            </div>
          )}
        </Tooltip>
      );
    }
    return <div className={styles.disabledWrapper}>{navItem}</div>;
  }

  if (isVisuallyDisabled) {
    return <div className={styles.visuallyDisabled}>{navItem}</div>;
  }

  if (isGreyedOut) {
    return <div className={styles.greyedOut}>{navItem}</div>;
  }

  return navItem;
}

export function MainNavigation() {
  const currentWorkspace = useCurrentWorkspace();
  const permissions = usePermissions();
  const auth = useAuth();
  const safeNavigate = useSafeNavigate();
  const journeysObjectTypes = useListWorkspaceObjectTypes(currentWorkspace?.id);
  const { track } = useAnalytics();
  const sideNavState = useSideNavState();
  const isCollapsedRef = useRef(sideNavState?.isCollapsed === true);
  isCollapsedRef.current = sideNavState?.isCollapsed === true;

  const { isSettingsPage } = useIsSettings();

  const onClickNavItem = useCallback(
    (event: React.MouseEvent | undefined, item: NavItemType, section: string | null, parentItem: string | null) => {
      if (event == null) {
        return;
      }

      if (!shouldProcessLinkClick(event)) return;

      event.preventDefault();

      void track('leftNav', 'itemClicked', {
        'Nav Item': item.label,
        'Nav Section': section ?? null,
        'Is Nested': parentItem != null,
        'Parent Item': parentItem ?? null,
        'Entry Point': isCollapsedRef.current ? 'collapsed_icon' : 'expanded',
      });

      if (item.href == null) return;

      safeNavigate(item.href, item.transitionOptions ?? {});
    },
    [safeNavigate, track],
  );

  // generating navigation items and groups
  const navItemGroups = useMemo(() => {
    let items: NavItemGroup[] = [];

    if (currentWorkspace != null) {
      const objectTypes = journeysObjectTypes.data ?? [];
      items = generateRoutes(currentWorkspace, auth, permissions, {
        objectTypes,
      });
    }

    if (
      isSettingsPage &&
      auth.user?.last_cio_workspace_id != null &&
      !isEmptyString(auth.user?.last_cio_workspace_id) &&
      currentWorkspace != null
    ) {
      items = generateRoutes(currentWorkspace, auth, permissions);
    }

    const groups = items.map((group, index) => {
      const navItems = group.items.map((item) => {
        return <ExtendedNavItem key={item.id} item={item} onClick={onClickNavItem} section={group.title ?? null} />;
      });

      return (
        <SideNavGroup key={group.title ?? `group_${index}`} title={group.title}>
          {navItems}
        </SideNavGroup>
      );
    });

    return groups;
  }, [auth, currentWorkspace, permissions, isSettingsPage, journeysObjectTypes.data, onClickNavItem]);

  return <>{navItemGroups}</>;
}
